import { useState } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode, Ref } from 'react';

export type InputSize = 'sm' | 'md' | 'lg';

const alturas: Record<InputSize, number> = { sm: 36, md: 42, lg: 60 };

function shell(focus: boolean, invalid: boolean, disabled: boolean, size: InputSize): CSSProperties {
    return {
        width: '100%', fontFamily: 'var(--font-ui)', fontSize: 14, lineHeight: '20px',
        color: 'var(--text-primary)', background: disabled ? 'var(--surface-secondary)' : 'var(--surface)',
        border: `1px solid ${invalid ? 'var(--danger)' : focus ? 'var(--border-focus)' : 'var(--border-strong)'}`,
        borderRadius: 'var(--radius-input)',
        boxShadow: focus ? (invalid ? '0 0 0 3px rgba(239,68,68,.18)' : 'var(--focus-ring)') : 'none',
        outline: 'none', transition: 'var(--transition-control)',
        height: alturas[size] ?? alturas.md,
        padding: '0 12px',
        cursor: disabled ? 'not-allowed' : 'auto',
    };
}

/* `onChange` entrega o valor, não o evento — é o que as 307 chamadas esperam.
   `size` é número no atributo nativo e escala aqui. Os dois são conflito real,
   então saem por Omit; o resto dos atributos de <input> é herdado. */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size' | 'value'> {
    value?: string | number;
    onChange?: (value: string) => void;
    size?: InputSize;
    invalid?: boolean;
    iconLeft?: ReactNode;
    suffix?: ReactNode;
    inputRef?: Ref<HTMLInputElement>;
    /** `id` do elemento que descreve o campo, para `aria-describedby`. */
    describedBy?: string;
    /** Estilo do invólucro, não do <input>. */
    style?: CSSProperties;
}

export function Input({
    value, onChange, placeholder, type = 'text', size = 'md', invalid = false, disabled = false,
    iconLeft, suffix, id, inputRef, describedBy, name, onBlur, onFocus, style, ...rest
}: InputProps) {
    const [focus, setFocus] = useState(false);
    const s = shell(focus, invalid, Boolean(disabled), size);
    return (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', ...style }}>
            {iconLeft && (
                <span style={{ position: 'absolute', left: 11, display: 'flex', color: 'var(--neutral-500)', pointerEvents: 'none' }}>{iconLeft}</span>
            )}
            <input
                id={id} ref={inputRef} name={name} type={type} value={value} placeholder={placeholder} disabled={disabled}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                onChange={(e) => onChange?.(e.target.value)}
                onFocus={(e) => { setFocus(true); onFocus?.(e); }}
                onBlur={(e) => { setFocus(false); onBlur?.(e); }}
                style={{ ...s, paddingLeft: iconLeft ? 34 : 12, paddingRight: suffix ? 40 : 12 }}
                {...rest}
            />
            {suffix && (
                <span style={{ position: 'absolute', right: 11, display: 'flex', color: 'var(--neutral-500)', fontSize: 13 }}>{suffix}</span>
            )}
        </div>
    );
}
